'use client';

import { logger } from '@/lib/logger';
import React, { useState } from 'react';
import {
  BadgeCheck,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useGetUser } from '@/hooks/useAuth';
import { usePrimeAccessPlans, usePurchasePrimeAccess } from '@/hooks/usePrimeAccess';
import { useUserBalances } from '@/hooks/useWallet';
import { useConnection, useSignMessage } from 'wagmi';
import { formatUnits } from 'viem';
import { TOKENS } from '@/config/tokens';
import { createSiweMessage, generateSecureNonce } from '@/lib/siwe';
import { useIdrToAsetqu } from '@/hooks/useCurrencyConversion';
import { usePiAccessConfig } from '@/hooks/usePiAccess';
import { TokenApprovalGuard } from '@/components/TokenApprovalGuard';
import { getReadableBlockchainError } from '@/lib/utils';

interface PrimeAccessModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
}

// Web3 token format: 6 significant figures, trailing zeros removed
// Uses toPrecision (not toFixed) to absorb floating-point noise (e.g. 1.000002 → 1)
const formatAsq = (amount: number): string => {
  if (amount === 0) return '0';
  return parseFloat(amount.toPrecision(6)).toString();
};

const PrimeAccessModal: React.FC<PrimeAccessModalProps> = ({ onClose, onSuccess }) => {
  const { data: user, refetch: refetchUser } = useGetUser();
  const { address } = useConnection();
  const { signMessageAsync } = useSignMessage();
  const { data: balances } = useUserBalances();

  const { data: plans, isLoading: loadingPlans } = usePrimeAccessPlans({ status: 'ACTIVE' });
  const { data: piConfig } = usePiAccessConfig();
  const purchaseMutation = usePurchasePrimeAccess();

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get the appropriate membership plan based on user tier
  const userTier = user?.currentMembershipTier || 'GENERAL';
  const availablePlan = plans?.find(p => p.tier === userTier);

  // Pick the IDR price for the user's tier
  const priceIdr = parseFloat(
    userTier === 'ASETRACLUB'
      ? (piConfig?.priceIdrAsetraclub ?? '0')
      : (piConfig?.priceIdr ?? '0')
  );

  // Without an IDR price the server charges the plan's own on-chain price
  // (priceRaw, in the token's smallest unit), so show exactly that amount.
  const planPriceRaw = BigInt(availablePlan?.priceRaw || '0');
  const usesPlanPrice = priceIdr === 0 && planPriceRaw > 0n;
  const planPriceLabel = formatUnits(planPriceRaw, TOKENS.ASETQU.decimals);

  // Free only when neither an IDR price nor a plan price is set
  const isFree = priceIdr === 0 && planPriceRaw === 0n;

  // Real-time IDR → ASETQU conversion via Indodax WebSocket
  const { convert, priceIdr: asetquPriceIdr, isConnected, isLoadingInitial, error: wsError } = useIdrToAsetqu();
  const priceAsq = priceIdr > 0 ? convert(priceIdr) : 0;

  // Check if user has sufficient balance. The plan price can be a single wei,
  // which a float comparison cannot represent, so compare raw integers there.
  const userBalance = parseFloat(balances?.tokenBalance.balanceFormatted || '0');
  const userBalanceRaw = BigInt(balances?.tokenBalance.balanceRaw || '0');
  const hasInsufficientBalance = usesPlanPrice
    ? userBalanceRaw < planPriceRaw
    : !isFree && priceAsq > 0 && userBalance < priceAsq;
  const requiredLabel = usesPlanPrice ? planPriceLabel : `≈ ${formatAsq(priceAsq)}`;

  const handlePurchase = async () => {
    if (!availablePlan || !address) {
      setError('Plan atau wallet tidak tersedia');
      return;
    }

    if (hasInsufficientBalance) {
      setError(
        `Saldo tidak cukup. Dibutuhkan ${requiredLabel} AsetQu, saldo kamu ${formatAsq(userBalance)} AsetQu`
      );
      return;
    }

    try {
      setError(null);
      setSelectedPlanId(availablePlan.id);

      // SIWE statement must be ASCII-only — use the real-time ASETQU amount the user
      // sees on screen. Intl.NumberFormat produces non-breaking space which breaks the SIWE ABNF parser.
      const priceLabel = isFree
        ? 'free'
        : `${usesPlanPrice ? planPriceLabel : formatAsq(priceAsq)} ASETQU`;
      const siweMessage = createSiweMessage({
        address,
        statement: `purchase membership: ${availablePlan.name} for ${priceLabel}`,
        nonce: generateSecureNonce(),
      });

      // Prepare SIWE formatted message
      const message = siweMessage.prepareMessage();

      // Sign with wallet
      const signature = await signMessageAsync({ message });

      // Submit purchase
      await purchaseMutation.mutateAsync({
        membershipId: availablePlan.id,
        message,
        signature,
      });

      // Show success
      setSuccess(true);

      // Refetch user to get updated tier
      await refetchUser();

      // Auto-close after 2 seconds
      setTimeout(() => {
        onSuccess?.();
        onClose?.();
      }, 2000);
    } catch (err: any) {
      logger.error('Purchase error:', err);
      setError(getReadableBlockchainError(err, 'Gagal membeli membership. Silakan coba lagi.'));
      setSelectedPlanId(null);
    }
  };

  if (loadingPlans) {
    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center px-6 animate-in fade-in duration-500'>
        <div className='absolute inset-0 bg-white/40 backdrop-blur-md transition-all' />
        <div className='relative z-10 w-full max-w-[400px] bg-white/60 backdrop-blur-xl border border-white/50 rounded-[3rem] shadow-[0_30px_60px_-10px_rgba(0,0,0,0.15)] p-12 text-center'>
          <Loader2 className='w-12 h-12 text-blue-600 animate-spin mx-auto mb-4' />
          <p className='text-sm text-gray-600'>Loading membership details...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center px-6 animate-in fade-in duration-500'>
        <div className='absolute inset-0 bg-white/40 backdrop-blur-md transition-all' />
        <div className='relative z-10 w-full max-w-[400px] bg-white/60 backdrop-blur-xl border border-white/50 rounded-[3rem] shadow-[0_30px_60px_-10px_rgba(0,0,0,0.15)] p-12 text-center'>
          <div className='w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6'>
            <CheckCircle2 className='w-12 h-12 text-green-600' />
          </div>
          <h3 className='text-xl font-bold text-gray-900 mb-2'>Welcome to ASET PRIME!</h3>
          <p className='text-sm text-gray-600'>
            {isFree
              ? 'Your membership has been activated.'
              : 'Payment successful! Your membership is now active.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center px-6 animate-in fade-in duration-500'>
      <div className='absolute inset-0 bg-white/40 backdrop-blur-md transition-all' />

      <div className='relative z-10 w-full max-w-[420px] bg-white/60 backdrop-blur-xl border border-white/50 rounded-[3rem] shadow-[0_30px_60px_-10px_rgba(0,0,0,0.15)] p-8 text-center animate-in zoom-in-95 duration-500'>
        {/* Logo */}
        <div className='mb-6 relative group cursor-pointer hover:scale-105 transition-transform duration-500'>
          <div className='absolute inset-0 bg-blue-500/30 rounded-full blur-2xl transform scale-110'></div>

          <div className='relative w-24 h-24 mx-auto rounded-full bg-linear-to-b from-blue-400 to-blue-700 p-1.5 shadow-2xl shadow-blue-600/20 flex items-center justify-center'>
            <div className='w-full h-full rounded-full border-[3px] border-white/20 bg-linear-to-br from-blue-600 to-blue-900 flex items-center justify-center relative overflow-hidden'>
              <div className='absolute top-0 left-0 w-full h-1/2 bg-white/10 rounded-t-full pointer-events-none'></div>

              <div className='relative w-16 h-16'>
                <img
                  src='/logo.png'
                  alt='AsetQu Prime'
                  className='w-full h-full object-contain drop-shadow-md'
                />
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <h2 className='text-xl font-black text-gray-900 mb-2 tracking-tight'>ASET PRIME ACCESS</h2>
        <p className='text-xs text-gray-600 mb-6'>
          {isFree
            ? 'Aktifkan membership gratis kamu untuk melanjutkan'
            : 'Buka akses fitur eksklusif dan premium'}
        </p>

        {/* Plan Details */}
        {availablePlan && (
          <div className='bg-white/50 rounded-2xl p-6 mb-6 border border-gray-200/50'>
            <div className='flex items-center justify-between mb-4'>
              <div>
                <p className='text-sm font-semibold text-gray-700'>Plan</p>
                <p className='text-lg font-bold text-gray-900'>Prime Access</p>
              </div>
              <div className='text-right'>
                <p className='text-sm font-semibold text-gray-700'>Harga</p>
                {isFree ? (
                  <p className='text-2xl font-black text-green-600'>GRATIS</p>
                ) : usesPlanPrice ? (
                  <p className='text-base font-black text-blue-600 break-all'>
                    {planPriceLabel} AsetQu
                  </p>
                ) : isLoadingInitial && asetquPriceIdr === 0 ? (
                  <Loader2 size={20} className='animate-spin text-blue-600 ml-auto mt-1' />
                ) : priceAsq > 0 ? (
                  <p className='text-2xl font-black text-blue-600'>{formatAsq(priceAsq)} AsetQu</p>
                ) : (
                  <p className='text-sm text-blue-600'>Menghitung...</p>
                )}
              </div>
            </div>

            {/* Live price status — only the IDR price depends on the live rate */}
            {!isFree && !usesPlanPrice && (
              <div className='flex items-center justify-center gap-2 mb-3'>
                {isConnected ? (
                  <>
                    <Wifi size={10} className='text-green-600 animate-pulse' />
                    <p className='text-[10px] text-green-600 font-medium'>Live • Harga real-time</p>
                  </>
                ) : (
                  <>
                    <WifiOff size={10} className='text-blue-600' />
                    <p className='text-[10px] text-blue-600 font-medium'>
                      Menghubungkan ke harga live...
                    </p>
                  </>
                )}
              </div>
            )}

            {/* WebSocket Error */}
            {!isFree && !usesPlanPrice && wsError && (
              <div className='bg-amber-50 rounded-xl p-3 mb-3 border border-amber-200'>
                <div className='flex items-center justify-center gap-2'>
                  <WifiOff size={14} className='text-amber-600' />
                  <p className='text-xs text-amber-700'>Tidak dapat terhubung ke feed harga</p>
                </div>
              </div>
            )}

            {userTier === 'ASETRACLUB' && (
              <div className='bg-linear-to-r from-green-50 to-emerald-50 rounded-xl p-3 border border-green-200'>
                <p className='text-xs font-semibold text-green-800 flex items-center justify-center gap-2'>
                  <BadgeCheck size={16} className='text-green-600' />
                  Gratis untuk Asetra Club Members
                </p>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className='bg-red-50 border border-red-200 rounded-xl p-4 mb-4'>
            <div className='flex items-start gap-3'>
              <AlertCircle className='w-5 h-5 text-red-600 shrink-0 mt-0.5' />
              <div className='text-left'>
                <p className='text-sm font-semibold text-red-900'>Purchase Failed</p>
                <p className='text-xs text-red-700 mt-1'>{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Insufficient Balance Warning */}
        {hasInsufficientBalance && (
          <div className='bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4'>
            <div className='flex items-start gap-3'>
              <AlertCircle className='w-5 h-5 text-amber-600 shrink-0 mt-0.5' />
              <div className='text-left'>
                <p className='text-sm font-semibold text-amber-900'>Saldo Tidak Cukup</p>
                <p className='text-xs text-amber-700 mt-1'>
                  Dibutuhkan {requiredLabel} AsetQu, saldo kamu {formatAsq(userBalance)} AsetQu
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Button */}
        <TokenApprovalGuard>
          <button
            onClick={handlePurchase}
            disabled={
              purchaseMutation.isPending ||
              !availablePlan ||
              hasInsufficientBalance ||
              (!isFree && !usesPlanPrice && priceAsq === 0)
            }
            className='w-full bg-linear-to-r from-[#00897B] to-[#1565C0] hover:scale-[1.02] active:scale-95 transition-all duration-300 text-white font-extrabold py-4 px-6 rounded-full shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {purchaseMutation.isPending ? (
              <>
                <Loader2 size={18} className='animate-spin' />
                <span className='text-xs tracking-widest uppercase font-black'>
                  {selectedPlanId ? 'PROCESSING...' : 'PLEASE WAIT...'}
                </span>
              </>
            ) : (
              <>
                <div className='bg-white/20 p-0.5 rounded-full'>
                  <BadgeCheck size={18} className='text-white' strokeWidth={3} />
                </div>
                <span className='text-xs tracking-widest uppercase font-black'>
                  {isFree ? 'ACTIVATE MEMBERSHIP' : 'PURCHASE MEMBERSHIP'}
                </span>
              </>
            )}
          </button>
        </TokenApprovalGuard>

        {/* Info Text */}
        <p className='text-xs text-gray-500 mt-4'>
          {isFree
            ? 'Klik untuk mengaktifkan membership gratis kamu dan akses semua fitur premium.'
            : usesPlanPrice
              ? `Kamu akan diminta menandatangani transaksi dengan wallet untuk membayar ${planPriceLabel} AsetQu.`
              : 'Kamu akan diminta menandatangani transaksi dengan wallet. Jumlah AsetQu dihitung dari harga real-time saat transaksi.'}
        </p>
      </div>
    </div>
  );
};

export default PrimeAccessModal;
